import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Images, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { MyMediaPickerDialog } from '@/components/media/MyMediaPickerDialog';

const LOGO_POSITIONS = [
  { value: 'top-left' },
  { value: 'top-center' },
  { value: 'top-right' },
  { value: 'center-left' },
  { value: 'center' },
  { value: 'center-right' },
  { value: 'bottom-left' },
  { value: 'bottom-center' },
  { value: 'bottom-right' },
];

export interface SubtitleEditorLogoData {
  url: string | null;
  position: string;
  size: number;
  opacity: number;
  horizontalMargin: number;
  verticalMargin: number;
}

export interface SubtitleLogoEditorProps {
  logoData: SubtitleEditorLogoData;
  onLogoChange: (field: keyof SubtitleEditorLogoData, value: string | number | null) => void;
  onLogoUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadingLogo: boolean;
}

export const SubtitleLogoEditor = ({
  logoData,
  onLogoChange,
  onLogoUpload,
  isUploadingLogo,
}: SubtitleLogoEditorProps) => {
  const { t } = useTranslation();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  return (
    <div className="space-y-3 p-4" data-testid="subtitle-logo-editor">
      <div className="flex items-center gap-2">
        <Image className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold">
          {t('configureSubtitles.editor.logo')}
        </span>
      </div>

      <Input
        ref={logoInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        onChange={onLogoUpload}
        className="hidden"
        id="subtitle-editor-logo-upload"
        disabled={isUploadingLogo}
      />

      {!logoData.url ? (
        <div className="rounded-lg border border-dashed border-white/10 bg-muted/20 p-3 text-center">
          <Upload className="mx-auto mb-1.5 h-5 w-5 text-muted-foreground" />
          <p className="mb-1.5 text-[11px] text-muted-foreground">PNG, JPG (5MB max)</p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              data-testid="subtitle-logo-my-media"
              onClick={() => setMediaPickerOpen(true)}
            >
              <Images className="mr-1.5 h-3.5 w-3.5" />
              {t('myMedia.picker.open')}
            </Button>
            <Label htmlFor="subtitle-editor-logo-upload">
              <Button variant="outline" size="sm" asChild disabled={isUploadingLogo}>
                <span>
                  <Upload className="mr-1.5 h-3.5 w-3.5" />
                  {isUploadingLogo
                    ? t('common.uploading')
                    : t('configureSubtitles.editor.uploadLogo')}
                </span>
              </Button>
            </Label>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-lg bg-muted/30 p-2">
          <img
            src={logoData.url}
            alt=""
            crossOrigin="anonymous"
            className="h-8 w-auto rounded"
          />
          <p className="min-w-0 flex-1 truncate text-xs font-medium">
            {t('configureSubtitles.editor.logoUploaded')}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            data-testid="subtitle-logo-replace-my-media"
            className="h-7 w-7"
            title={t('myMedia.picker.open')}
            onClick={() => setMediaPickerOpen(true)}
          >
            <Images className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title={t('configureSubtitles.editor.uploadLogo')}
            disabled={isUploadingLogo}
            onClick={() => logoInputRef.current?.click()}
          >
            <Upload className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={() => onLogoChange('url', null)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      <MyMediaPickerDialog
        open={mediaPickerOpen}
        onOpenChange={setMediaPickerOpen}
        onSelect={(item) => onLogoChange('url', item.url)}
        onUploadInstead={() => {
          setMediaPickerOpen(false);
          logoInputRef.current?.click();
        }}
      />

      {logoData.url && (
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">
              {t('configureSubtitles.editor.logoPosition')}
            </Label>
            <div className="grid grid-cols-3 gap-1">
              {LOGO_POSITIONS.map((pos) => (
                <Button
                  key={pos.value}
                  variant={logoData.position === pos.value ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-[10px]"
                  onClick={() => onLogoChange('position', pos.value)}
                >
                  {pos.value}
                </Button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                {t('configureSubtitles.editor.logoOpacity')}: {logoData.opacity}%
              </Label>
              <Slider
                value={[logoData.opacity]}
                onValueChange={([v]) => onLogoChange('opacity', v)}
                min={0}
                max={100}
                step={1}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                {t('configureSubtitles.editor.logoSize')}: {logoData.size}%
              </Label>
              <Slider
                value={[logoData.size]}
                onValueChange={([v]) => onLogoChange('size', v)}
                min={1}
                max={100}
                step={1}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                {t('configureSubtitles.editor.hMargin')}: {logoData.horizontalMargin}%
              </Label>
              <Slider
                value={[logoData.horizontalMargin]}
                onValueChange={([v]) => onLogoChange('horizontalMargin', v)}
                min={0}
                max={50}
                step={1}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                {t('configureSubtitles.editor.vMargin')}: {logoData.verticalMargin}%
              </Label>
              <Slider
                value={[logoData.verticalMargin]}
                onValueChange={([v]) => onLogoChange('verticalMargin', v)}
                min={0}
                max={50}
                step={1}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
